import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface HeroSectionProps {
  eyebrow?: string;
  title: string;
  subtitle: string;
  primaryCTA?: string;
  secondaryCTA?: string;
  secondaryCTALink?: string;
  imageSrc?: string;
}

export default function HeroSection({
  eyebrow = 'Philadelphia, PA · Premium Automotive Protection',
  title,
  subtitle,
  primaryCTA = 'Get a Free Quote',
  secondaryCTA,
  secondaryCTALink = '/services',
  imageSrc = 'https://public.readdy.ai/ai/img_res/b10b2c9dd92e4ec643dc49280874cb07.jpg',
}: HeroSectionProps) {
  const [visible, setVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 150);
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { clearTimeout(t); window.removeEventListener('scroll', onScroll); };
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden bg-[#080808]">
      {/* Parallax background */}
      <div
        className="absolute inset-0 scale-110"
        style={{ transform: `translateY(${scrollY * 0.2}px) scale(1.1)` }}
      >
        <img
          src={imageSrc}
          alt="Premium automotive window tinting and ceramic coating"
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Layered overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#080808] via-[#080808]/75 to-[#080808]/15" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-[#080808]/35" />

      {/* Gold vertical accent */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-[#C9A063]/50 to-transparent hidden lg:block" />

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-32 pb-24 md:pb-32">
        <div className="max-w-2xl xl:max-w-3xl">
          {/* Eyebrow */}
          <div
            className={`flex items-center gap-3 mb-8 transition-all duration-[1200ms] ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="w-10 h-[1px] bg-[#C9A063]" />
            <span className="text-[#C9A063] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">
              {eyebrow}
            </span>
          </div>

          {/* Headline */}
          <h1
            className={`font-serif font-bold text-white leading-[1.05] mb-7 transition-all duration-[1200ms] delay-150 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ fontSize: 'clamp(2.4rem, 5vw, 4.5rem)' }}
          >
            {title}
          </h1>

          {/* Subtitle */}
          <p
            className={`text-white/60 font-sans font-light text-base md:text-lg leading-relaxed mb-12 max-w-lg transition-all duration-[1200ms] delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {subtitle}
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row gap-4 mb-10 transition-all duration-[1200ms] delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <a
              href="#estimate"
              className="whitespace-nowrap group flex items-center justify-center gap-2.5 bg-[#AAFF00] text-[#080808] text-sm font-sans font-semibold px-8 py-4 rounded-full hover:bg-[#C4FF33] transition-all duration-300 cursor-pointer"
            >
              {primaryCTA}
              <div className="w-5 h-5 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </a>
            {secondaryCTA && (
              <Link
                to={secondaryCTALink}
                className="whitespace-nowrap flex items-center justify-center gap-2 border border-white/25 text-white text-sm font-sans font-medium px-8 py-4 rounded-full hover:border-[#C9A063]/60 hover:text-[#C9A063] transition-all duration-300"
              >
                {secondaryCTA}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-line text-xs"></i>
                </div>
              </Link>
            )}
          </div>

          {/* Mobile tap-to-call */}
          <div
            className={`flex sm:hidden transition-all duration-[1200ms] delay-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <a
              href="tel:2155949404"
              className="flex items-center gap-3 border border-[#C9A063]/30 rounded-full px-6 py-3 text-[#C9A063] text-sm font-sans cursor-pointer hover:bg-[#C9A063]/10 transition-colors"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-base"></i>
              </div>
              <span>Tap to Call · (215) 594-9404</span>
            </a>
          </div>

          {/* Trust signals */}
          <div
            className={`hidden sm:flex items-center gap-6 mt-10 pt-10 border-t border-white/10 transition-all duration-[1200ms] delay-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="flex items-center gap-1.5">
              {[1,2,3,4,5].map((s) => (
                <div key={s} className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-[#C9A063] text-sm"></i>
                </div>
              ))}
              <span className="text-white/50 text-xs font-sans ml-1">5.0 · Google Reviews</span>
            </div>
            <div className="w-[1px] h-4 bg-white/15" />
            <span className="text-white/40 text-xs font-sans">Philadelphia&apos;s Premier Tint Studio</span>
            <div className="w-[1px] h-4 bg-white/15 hidden md:block" />
            <a
              href="tel:2155949404"
              className="hidden md:flex items-center gap-2 text-white/45 text-xs font-sans hover:text-[#C9A063] transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-[#C9A063] text-xs"></i>
              </div>
              (215) 594-9404
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 right-12 hidden lg:flex flex-col items-center gap-3">
        <div className="flex flex-col gap-[3px]">
          {[0,1,2].map((i) => (
            <div key={i} className="w-[1px] h-3 bg-[#C9A063]/50 mx-auto" style={{ opacity: 1 - i * 0.25 }} />
          ))}
        </div>
        <span className="text-white/25 text-[9px] font-sans tracking-[0.3em] uppercase" style={{ writingMode: 'vertical-rl' }}>
          Scroll
        </span>
      </div>
    </section>
  );
}
